import { useMemo } from 'react';
import { NODE_TYPES, NODE_CATEGORIES, DEFAULT_NODE_CONFIGS } from '../utils/constants';

// Import node components
import InputNode from '../nodes/basic/InputNode';
import DenseNode from '../nodes/basic/DenseNode';
import DropoutNode from '../nodes/basic/DropoutNode';
import OutputNode from '../nodes/basic/OutputNode';
import LSTMNode from '../nodes/recurrent/LSTMNode';
import MultiHeadAttentionNode from '../nodes/attention/MultiHeadAttentionNode';
import Conv1DNode from '../nodes/convolutional/Conv1DNode';
import ConcatenateNode from '../nodes/utility/ConcatenateNode';

// Import icons (for the node toolbox)
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import GridOnIcon from '@mui/icons-material/GridOn';
import FunctionsIcon from '@mui/icons-material/Functions';
import MemoryIcon from '@mui/icons-material/Memory';
import FilterIcon from '@mui/icons-material/Filter';
import VisibilityIcon from '@mui/icons-material/Visibility';
import LayersIcon from '@mui/icons-material/Layers';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import CodeIcon from '@mui/icons-material/Code';
import CategoryIcon from '@mui/icons-material/Category';
import TimelineIcon from '@mui/icons-material/Timeline';
import RepeatIcon from '@mui/icons-material/Repeat';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TextFormatIcon from '@mui/icons-material/TextFormat';
import ScienceIcon from '@mui/icons-material/Science';
import FormatShapesIcon from '@mui/icons-material/FormatShapes';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AddIcon from '@mui/icons-material/Add';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import CodeOffIcon from '@mui/icons-material/CodeOff';

/**
 * Hook to manage node types and their configurations
 */
const useNodeTypes = () => {
  // Define node type components map
  const nodeTypes = useMemo(() => ({
    // Basic node types
    inputNode: InputNode,
    denseNode: DenseNode,
    dropoutNode: DropoutNode,
    outputNode: OutputNode,
    
    // Recurrent nodes
    lstmNode: LSTMNode,
    // gruNode: GRUNode, // To be implemented similarly to LSTMNode
    // rnnNode: RNNNode, // To be implemented
    // bidirectionalNode: BidirectionalNode, // To be implemented
    // timeDistributedNode: TimeDistributedNode, // To be implemented
    
    // Attention nodes
    multiHeadAttentionNode: MultiHeadAttentionNode,
    // selfAttentionNode: SelfAttentionNode, // To be implemented
    // transformerEncoderNode: TransformerEncoderNode, // To be implemented
    
    // Convolutional nodes
    conv1dNode: Conv1DNode,
    // dilatedConv1dNode: DilatedConv1DNode, // To be implemented
    // maxPooling1dNode: MaxPooling1DNode, // To be implemented
    
    // Utility nodes
    concatenateNode: ConcatenateNode,
    // reshapeNode: ReshapeNode, // To be implemented
    // flattenNode: FlattenNode, // To be implemented
  }), []);
  
  // Node type to component type mapping
  const nodeTypeMap = useMemo(() => ({
    [NODE_TYPES.INPUT]: 'inputNode',
    [NODE_TYPES.OUTPUT]: 'outputNode',
    [NODE_TYPES.DENSE]: 'denseNode',
    [NODE_TYPES.DROPOUT]: 'dropoutNode',
    [NODE_TYPES.LSTM]: 'lstmNode',
    [NODE_TYPES.MULTI_HEAD_ATTENTION]: 'multiHeadAttentionNode',
    [NODE_TYPES.CONV_1D]: 'conv1dNode',
    [NODE_TYPES.CONCATENATE]: 'concatenateNode',
    
    // Default to existing components for others until specific implementations are created
    [NODE_TYPES.GRU]: 'lstmNode',
    [NODE_TYPES.RNN]: 'lstmNode',
    [NODE_TYPES.BIDIRECTIONAL]: 'lstmNode',
    [NODE_TYPES.TIME_DISTRIBUTED]: 'lstmNode',
    [NODE_TYPES.REPEAT_VECTOR]: 'lstmNode',
    [NODE_TYPES.MASKING]: 'lstmNode',
    
    [NODE_TYPES.SELF_ATTENTION]: 'multiHeadAttentionNode',
    [NODE_TYPES.POSITIONAL_ENCODING]: 'multiHeadAttentionNode',
    [NODE_TYPES.TRANSFORMER_ENCODER]: 'multiHeadAttentionNode',
    [NODE_TYPES.TRANSFORMER_DECODER]: 'multiHeadAttentionNode',
    [NODE_TYPES.CAUSAL_MASK]: 'multiHeadAttentionNode',
    
    [NODE_TYPES.DILATED_CONV_1D]: 'conv1dNode',
    [NODE_TYPES.CONV_2D]: 'conv1dNode',
    [NODE_TYPES.MAX_POOLING_1D]: 'conv1dNode',
    [NODE_TYPES.AVERAGE_POOLING_1D]: 'conv1dNode',
    [NODE_TYPES.GLOBAL_MAX_POOLING_1D]: 'conv1dNode',
    [NODE_TYPES.GLOBAL_AVERAGE_POOLING_1D]: 'conv1dNode',
    
    [NODE_TYPES.FLATTEN]: 'concatenateNode',
    [NODE_TYPES.RESHAPE]: 'concatenateNode',
    [NODE_TYPES.PERMUTE]: 'concatenateNode',
    [NODE_TYPES.ADD]: 'concatenateNode',
    [NODE_TYPES.MULTIPLY]: 'concatenateNode',
    [NODE_TYPES.SPLIT]: 'concatenateNode',
    [NODE_TYPES.SLICE]: 'concatenateNode',
    [NODE_TYPES.LAMBDA]: 'concatenateNode',
    [NODE_TYPES.EMBEDDING]: 'concatenateNode',
    [NODE_TYPES.CUSTOM_LAYER]: 'concatenateNode',
    [NODE_TYPES.CONDITION]: 'concatenateNode',
    [NODE_TYPES.ACTIVATION]: 'concatenateNode',
    [NODE_TYPES.BATCH_NORMALIZATION]: 'concatenateNode',
    [NODE_TYPES.LAYER_NORMALIZATION]: 'concatenateNode'
  }), []);
  
  // Node categories with icons and node types
  const nodeCategories = useMemo(() => [
    {
      id: NODE_CATEGORIES.INPUT_OUTPUT,
      label: 'Input & Output',
      icon: <CategoryIcon />,
      nodeTypes: [
        // Removed Input and Output nodes since they're already fixed on the canvas
      ],
    },
    {
      id: NODE_CATEGORIES.CORE,
      label: 'Core Layers',
      icon: <LayersIcon />,
      nodeTypes: [
        {
          type: NODE_TYPES.DENSE,
          label: 'Dense',
          icon: <GridOnIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.DENSE],
          description: 'Fully connected layer',
        },
        {
          type: NODE_TYPES.ACTIVATION,
          label: 'Activation',
          icon: <FunctionsIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.ACTIVATION],
          description: 'Apply activation function',
        },
        {
          type: NODE_TYPES.DROPOUT,
          label: 'Dropout',
          icon: <LayersIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.DROPOUT],
          description: 'Prevents overfitting',
        },
        {
          type: NODE_TYPES.BATCH_NORMALIZATION,
          label: 'BatchNorm',
          icon: <LayersIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.BATCH_NORMALIZATION],
          description: 'Normalize activations',
        },
        {
          type: NODE_TYPES.LAYER_NORMALIZATION,
          label: 'LayerNorm',
          icon: <LayersIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.LAYER_NORMALIZATION],
          description: 'Layer normalization',
        }
      ],
    },
    {
      id: NODE_CATEGORIES.RECURRENT,
      label: 'Recurrent Layers',
      icon: <MemoryIcon />,
      nodeTypes: [
        {
          type: NODE_TYPES.LSTM,
          label: 'LSTM',
          icon: <MemoryIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.LSTM],
          description: 'Long Short-Term Memory layer',
        },
        {
          type: NODE_TYPES.GRU,
          label: 'GRU',
          icon: <MemoryIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.GRU],
          description: 'Gated Recurrent Unit layer',
        },
        {
          type: NODE_TYPES.RNN,
          label: 'RNN',
          icon: <MemoryIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.RNN],
          description: 'Simple Recurrent Neural Network',
        },
        {
          type: NODE_TYPES.BIDIRECTIONAL,
          label: 'Bidirectional',
          icon: <SwapHorizIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.BIDIRECTIONAL],
          description: 'Bidirectional wrapper for RNNs',
        },
        {
          type: NODE_TYPES.TIME_DISTRIBUTED,
          label: 'TimeDistributed',
          icon: <TimelineIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.TIME_DISTRIBUTED],
          description: 'Apply layer to each time step',
        },
        {
          type: NODE_TYPES.REPEAT_VECTOR,
          label: 'RepeatVector',
          icon: <RepeatIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.REPEAT_VECTOR],
          description: 'Repeat input n times',
        },
        {
          type: NODE_TYPES.MASKING,
          label: 'Masking',
          icon: <VisibilityIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.MASKING],
          description: 'Mask timesteps in sequence data',
        }
      ],
    },
    {
      id: NODE_CATEGORIES.ATTENTION,
      label: 'Attention & Transformers',
      icon: <AutoAwesomeIcon />,
      nodeTypes: [
        {
          type: NODE_TYPES.SELF_ATTENTION,
          label: 'SelfAttention',
          icon: <AutoAwesomeIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.SELF_ATTENTION],
          description: 'Self-attention mechanism',
        },
        {
          type: NODE_TYPES.MULTI_HEAD_ATTENTION,
          label: 'MultiHeadAttention',
          icon: <PeopleAltIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.MULTI_HEAD_ATTENTION],
          description: 'Multi-head attention layer',
        },
        {
          type: NODE_TYPES.POSITIONAL_ENCODING,
          label: 'PositionalEncoding',
          icon: <TextFormatIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.POSITIONAL_ENCODING],
          description: 'Positional encoding for sequences',
        },
        {
          type: NODE_TYPES.TRANSFORMER_ENCODER,
          label: 'TransformerEncoder',
          icon: <AutoAwesomeIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.TRANSFORMER_ENCODER],
          description: 'Transformer encoder block',
        },
        {
          type: NODE_TYPES.TRANSFORMER_DECODER,
          label: 'TransformerDecoder',
          icon: <AutoAwesomeIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.TRANSFORMER_DECODER],
          description: 'Transformer decoder block',
        },
        {
          type: NODE_TYPES.CAUSAL_MASK,
          label: 'CausalMask',
          icon: <VisibilityIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.CAUSAL_MASK],
          description: 'Mask for causal attention',
        }
      ],
    },
    {
      id: NODE_CATEGORIES.CONVOLUTIONAL,
      label: 'Convolutional Layers',
      icon: <FilterIcon />,
      nodeTypes: [
        {
          type: NODE_TYPES.CONV_1D,
          label: 'Conv1D',
          icon: <FilterIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.CONV_1D],
          description: '1D convolutional layer',
        },
        {
          type: NODE_TYPES.DILATED_CONV_1D,
          label: 'DilatedConv1D',
          icon: <FilterIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.DILATED_CONV_1D],
          description: 'Dilated 1D convolution',
        },
        {
          type: NODE_TYPES.CONV_2D,
          label: 'Conv2D',
          icon: <FilterIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.CONV_2D],
          description: '2D convolutional layer',
        },
        {
          type: NODE_TYPES.MAX_POOLING_1D,
          label: 'MaxPooling1D',
          icon: <ViewStreamIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.MAX_POOLING_1D],
          description: 'Max pooling for 1D data',
        },
        {
          type: NODE_TYPES.AVERAGE_POOLING_1D,
          label: 'AvgPooling1D',
          icon: <ViewStreamIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.AVERAGE_POOLING_1D],
          description: 'Average pooling for 1D data',
        },
        {
          type: NODE_TYPES.GLOBAL_MAX_POOLING_1D,
          label: 'GlobalMaxPooling1D',
          icon: <ViewStreamIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.GLOBAL_MAX_POOLING_1D],
          description: 'Global max pooling for 1D data',
        },
        {
          type: NODE_TYPES.GLOBAL_AVERAGE_POOLING_1D,
          label: 'GlobalAvgPooling1D',
          icon: <ViewStreamIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.GLOBAL_AVERAGE_POOLING_1D],
          description: 'Global average pooling for 1D data',
        }
      ],
    },
    {
      id: NODE_CATEGORIES.DATA_MANIPULATION,
      label: 'Data Manipulation',
      icon: <FormatShapesIcon />,
      nodeTypes: [
        {
          type: NODE_TYPES.FLATTEN,
          label: 'Flatten',
          icon: <LayersIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.FLATTEN],
          description: 'Flattens the input',
        },
        {
          type: NODE_TYPES.RESHAPE,
          label: 'Reshape',
          icon: <FormatShapesIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.RESHAPE],
          description: 'Reshapes the input tensor',
        },
        {
          type: NODE_TYPES.PERMUTE,
          label: 'Permute',
          icon: <ShuffleIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.PERMUTE],
          description: 'Permutes dimensions of input',
        },
        {
          type: NODE_TYPES.CONCATENATE,
          label: 'Concatenate',
          icon: <CallMergeIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.CONCATENATE],
          description: 'Concatenate tensors along an axis',
        },
        {
          type: NODE_TYPES.ADD,
          label: 'Add',
          icon: <AddIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.ADD],
          description: 'Element-wise addition layer',
        },
        {
          type: NODE_TYPES.MULTIPLY,
          label: 'Multiply',
          icon: <MergeTypeIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.MULTIPLY],
          description: 'Element-wise multiplication',
        },
        {
          type: NODE_TYPES.SPLIT,
          label: 'Split',
          icon: <ContentCutIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.SPLIT],
          description: 'Split tensor along axis',
        },
        {
          type: NODE_TYPES.SLICE,
          label: 'Slice',
          icon: <ContentCutIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.SLICE],
          description: 'Extract slice from input',
        }
      ],
    },
    {
      id: NODE_CATEGORIES.UTILITY,
      label: 'Advanced Layers',
      icon: <CodeIcon />,
      nodeTypes: [
        {
          type: NODE_TYPES.LAMBDA,
          label: 'Lambda',
          icon: <CodeIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.LAMBDA],
          description: 'Custom lambda function',
        },
        {
          type: NODE_TYPES.EMBEDDING,
          label: 'Embedding',
          icon: <TextFormatIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.EMBEDDING],
          description: 'Word embedding layer',
        },
        {
          type: NODE_TYPES.CUSTOM_LAYER,
          label: 'CustomLayer',
          icon: <ScienceIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.CUSTOM_LAYER],
          description: 'User-defined custom layer',
        },
        {
          type: NODE_TYPES.CONDITION,
          label: 'Condition',
          icon: <HelpOutlineIcon />,
          config: DEFAULT_NODE_CONFIGS[NODE_TYPES.CONDITION],
          description: 'Conditional control flow',
        }
      ],
    },
  ], []);
  
  // Get component type from node type
  const getComponentType = (nodeType) => {
    return nodeTypeMap[nodeType] || 'denseNode'; // Default to dense if not found
  };
  
  // Get node config from node type
  const getNodeConfig = (nodeType) => {
    return DEFAULT_NODE_CONFIGS[nodeType] || {};
  };
  
  return {
    nodeTypes,
    nodeCategories,
    getComponentType,
    getNodeConfig,
  };
};

export default useNodeTypes; 